import axios, { AxiosError, type AxiosInstance, type AxiosResponse } from 'axios';
import { v4 as uuidv4 } from 'uuid';

import { getDevIdentity, isDevLoginEnabled } from '@/auth/devLogin';

const CORRELATION_HEADER = 'x-correlation-id';
const DEV_IDENTITY_HEADER = 'X-Dev-Identity';
const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * BMO's session-scoped correlation header. Unlike {@link CORRELATION_HEADER} — a fresh uuid per
 * HTTP request — this one is constant for an entire advisor form session: one value per browser
 * tab, held for as long as the advisor stays on the same form. It gives BMO a single key that
 * ties an advisor's whole sitting together, first save through submit.
 *
 * Deliberately NOT set by the request interceptor below. It belongs only on calls that reach a
 * BMO API, so callers pass it explicitly — see `applicationsApi.save` / `.get`, the only two
 * today. An interceptor would stamp every originator, workflow and auth call made while a form
 * happens to be open, and would need a shared mutable global to read from, which is exactly what
 * the advisor runtime's multi-tab isolation avoids (AdvisorApplicationPage's header comment).
 */
export const IDP_CORRELATION_HEADER = 'x-bmo-idp-correlation-id';

/**
 * Header bag carrying {@link IDP_CORRELATION_HEADER}, or an empty one when there is no id.
 *
 * Absent means absent: the header is omitted rather than sent empty, matching how the BFF
 * forwards it onward (`IdpCorrelationForwardingHandler`) and what ENGINEERING_STANDARDS.md
 * requires of browser context on BMO calls — omit it, never fabricate it.
 */
export function idpCorrelationHeader(id?: string): Record<string, string> {
  return id ? { [IDP_CORRELATION_HEADER]: id } : {};
}

/**
 * Timeout for endpoints that carry file bytes (ADR-0028), overriding {@link DEFAULT_TIMEOUT_MS}
 * per request. A ~4 MiB transfer over a corporate WAN can outrun 30 s, and the failure looks
 * identical to a hung API. Raised only on those calls rather than globally, so an ordinary JSON
 * endpoint that stops responding still fails fast.
 */
export const FILE_TRANSFER_TIMEOUT_MS = 120_000;

/** Error code on the synthetic failure raised for an HTML body on a JSON endpoint. */
export const ERR_HTML_FOR_JSON = 'ERR_HTML_FOR_JSON';

function contentTypeOf(response: AxiosResponse): string {
  const headers = response.headers as unknown as Record<string, unknown> | undefined;
  return String(headers?.['content-type'] ?? '');
}

/** A fresh uuid per HTTP request, on every client. */
function attachCorrelationHeader(instance: AxiosInstance): void {
  instance.interceptors.request.use((config) => {
    config.headers.set(CORRELATION_HEADER, uuidv4());
    return config;
  });
}

/**
 * Dev Login mode (localhost + Dev): tell the BE which synthetic identity is calling.
 * BE looks the id up in DevLoginIdentities and applies the matching oid + name + role.
 */
function attachDevIdentityHeader(instance: AxiosInstance): void {
  instance.interceptors.request.use((config) => {
    if (isDevLoginEnabled()) {
      const identity = getDevIdentity();
      if (identity) config.headers.set(DEV_IDENTITY_HEADER, identity.id);
    }
    return config;
  });
}

function attachHtmlForJsonGuard(instance: AxiosInstance): void {
  // CloudFront's SPA fallback (web.yaml CustomErrorResponses) rewrites ANY 404 from the
  // API origin into `200` + index.html. CloudFront applies that mapping distribution-wide
  // and offers no way to scope it per cache behavior, so /api/* is caught by it too and
  // every API 404 reaches us as a SUCCESSFUL HTML response — tracked as B17.
  //
  // Left unguarded, callers destructure fields off that HTML body, read `undefined`, and
  // carry it into the next call. That is how a 404 on POST /workflows/{id}/upload-url
  // became a PUT to the literal path "undefined" and surfaced to the user as a CloudFront
  // 403, three hops from its cause (2026-08-14).
  //
  // A 2xx carrying HTML is unambiguous here: nothing behind apiClient returns HTML on
  // success, and genuine error pages — WAF blocks, 5xx — keep their own status codes and are
  // rejected by axios before this runs. So the only way to land here is the 404 rewrite, which
  // is why the synthetic response restores status 404: that IS what the origin returned.
  //
  // This now also guards the BINARY endpoints (ADR-0028): `downloadFile` sets
  // responseType 'blob', but the check reads the response HEADER rather than the body, so a
  // rewritten 404 is still caught. That is precisely why every download must route through
  // apiClient — a bare fetch would hand the SPA shell to pdf.js as if it were a PDF.
  //
  // Keep this even after B17 is fixed. It costs one header check and it is the difference
  // between a loud failure at the call site and a silent `undefined` three hops away.
  instance.interceptors.response.use((response) => {
    if (!contentTypeOf(response).includes('text/html')) return response;

    throw new AxiosError(
      `Expected JSON from ${response.config.url ?? 'the API'} but received an HTML page. ` +
        'The API returned 404 and CloudFront rewrote it to 200 + index.html (B17).',
      ERR_HTML_FOR_JSON,
      response.config,
      response.request,
      { ...response, status: 404, statusText: 'Not Found' },
    );
  });
}

/**
 * @param withCredentials send the HttpOnly `bmo_session` cookie (the whole auth story — this
 *   is a BFF, the browser never holds a token).
 * @param devIdentity stamp {@link DEV_IDENTITY_HEADER} when Dev Login is on.
 */
function createApiClient({
  withCredentials,
  devIdentity,
}: {
  withCredentials: boolean;
  devIdentity: boolean;
}): AxiosInstance {
  const instance = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL ?? '/api/v1',
    timeout: DEFAULT_TIMEOUT_MS,
    headers: { 'Content-Type': 'application/json' },
    withCredentials,
  });

  attachCorrelationHeader(instance);
  if (devIdentity) attachDevIdentityHeader(instance);
  attachHtmlForJsonGuard(instance);

  return instance;
}

export const apiClient = createApiClient({ withCredentials: true, devIdentity: true });

/**
 * The ANONYMOUS client, for the public customer runtime's two endpoints
 * (`/ext/workflows/{id}/new` — see `api/publicWorkflows.ts`).
 *
 * Same base URL, same correlation header, and — crucially — the same
 * {@link attachHtmlForJsonGuard}. That guard is the whole reason this is an axios instance
 * rather than a bare `fetch`: 404 is the fail-closed answer for seven distinct conditions on
 * the public endpoints, and CloudFront rewrites every one of them into `200` + index.html
 * (B17). Unguarded, the template read would hand the SPA shell to the form renderer and the
 * PDF call would hand `index.html` to the customer's download as if it were a PDF.
 *
 * Two deliberate differences from {@link apiClient}:
 *
 * - `withCredentials: false` — the `bmo_session` cookie has no business on an anonymous
 *   route, and withholding it means the public endpoints behave identically for a stranger
 *   and for a signed-in staff member who happens to open an `/ext` URL. Left on, the only
 *   configuration we would ever exercise is the one where a cookie happens to be present.
 * - no {@link DEV_IDENTITY_HEADER} — so a developer with Dev Login enabled testing `/ext` is
 *   genuinely anonymous rather than a synthetic advisor, which is the only way the public
 *   gates get tested at all locally.
 */
export const publicApiClient = createApiClient({ withCredentials: false, devIdentity: false });
